import React, { useState } from 'react';
import { AnnouncementLayout, UpdateBlock } from '../user/AnnouncementLayout';
import { AnnouncementGallery } from './AnnouncementGallery';
import { saveAnnouncementMedia, type GalleryItem } from './announcementMedia';

export type BuilderElement = UpdateBlock & {
  imagePath?: string | null;
  thumbPath?: string | null;
  videoPath?: string | null;
  posterPath?: string | null;
  left?: BuilderElement[];
  right?: BuilderElement[];
};

const PALETTE: { type: BuilderElement['type']; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'text', label: 'Text' },
  { type: 'image', label: 'Image' },
  { type: 'media', label: 'Text + image' },
  { type: 'button', label: 'Button' },
  { type: 'video', label: 'Video' },
  { type: 'divider', label: 'Divider' },
  { type: 'columns', label: 'Two columns' },
];

function blank(type: BuilderElement['type']): BuilderElement {
  if (type === 'heading') return { type, text: 'Heading', level: 2 };
  if (type === 'text') return { type, text: '' };
  if (type === 'image') return { type, imagePath: '', imageAlt: '', imageUrl: '' };
  if (type === 'media') return { type, text: '', imagePath: '', imageAlt: '', imageUrl: '', placement: 'left' };
  if (type === 'button') return { type, label: 'Learn more', url: 'https://' };
  if (type === 'video') return { type, url: '' };
  if (type === 'columns') return { type, left: [{ type: 'text', text: '' }], right: [{ type: 'text', text: '' }] };
  return { type: 'divider' };
}

function fromGallery(item: GalleryItem): BuilderElement {
  if (item.kind === 'video' || item.type === 'video') {
    return {
      type: 'video',
      videoPath: item.videoPath,
      posterPath: item.posterPath,
      videoUrl: item.videoUrl,
      posterUrl: item.posterUrl,
    };
  }
  return {
    type: 'image',
    imagePath: item.imagePath,
    thumbPath: item.thumbPath,
    imageUrl: item.imageUrl,
    fullUrl: item.fullUrl,
    imageAlt: item.imageAlt || '',
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: '10px', color: '#f9fafb', fontWeight: 600 }}>
      {label}
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: '6px',
  boxSizing: 'border-box',
  background: '#0b0b0b',
  color: '#f9fafb',
  border: '1px solid #374151',
  borderRadius: '8px',
  padding: '10px 12px',
};

export const AnnouncementBuilder: React.FC<{
  blocks: BuilderElement[];
  onChange: (blocks: BuilderElement[]) => void;
  onError: (message: string) => void;
}> = ({ blocks, onChange, onError }) => {
  const [selected, setSelected] = useState(0);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const current = blocks[selected] || null;

  const update = (index: number, patch: Partial<BuilderElement>) => {
    onChange(blocks.map((block, blockIndex) => (blockIndex === index ? { ...block, ...patch } : block)));
  };

  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= blocks.length) return;
    const copy = [...blocks];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    onChange(copy);
    setSelected(next);
  };

  const upload = async (index: number, file?: File) => {
    if (!file) return;
    try {
      const result = await saveAnnouncementMedia(file);
      const next = fromGallery(result);
      update(index, next.type === 'video'
        ? { videoPath: next.videoPath, posterPath: next.posterPath, videoUrl: next.videoUrl, posterUrl: next.posterUrl }
        : { imagePath: next.imagePath, thumbPath: next.thumbPath, imageUrl: next.imageUrl, fullUrl: next.fullUrl });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'The image could not be saved.');
    }
  };

  const insertAt = (index: number, item: GalleryItem) => {
    const copy = [...blocks];
    copy.splice(index, 0, fromGallery(item));
    onChange(copy);
    setSelected(index);
  };

  const dropOnRow = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    setDropIndex(null);
    const media = event.dataTransfer.getData('application/x-aureus-media');
    if (media) {
      try {
        insertAt(index, JSON.parse(media));
      } catch {
        onError('That gallery item could not be added.');
      }
      return;
    }
    const from = Number(event.dataTransfer.getData('text/aureus-index'));
    if (!Number.isInteger(from) || from < 0 || from >= blocks.length || from === index) return;
    const copy = [...blocks];
    const [item] = copy.splice(from, 1);
    copy.splice(index, 0, item);
    onChange(copy);
    setSelected(index);
  };

  return (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        const media = event.dataTransfer.getData('application/x-aureus-media');
        if (!media) return;
        event.preventDefault();
        try {
          insertAt(blocks.length, JSON.parse(media));
        } catch {
          onError('That gallery item could not be added.');
        }
      }}
    >
      <AnnouncementGallery onInsert={(item) => insertAt(blocks.length, item)} onError={onError} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        {PALETTE.map((item) => (
          <button
            key={item.type}
            type="button"
            onClick={() => {
              onChange([...blocks, blank(item.type)]);
              setSelected(blocks.length);
            }}
            style={{ background: '#1f2937', color: '#f9fafb', border: '1px solid #374151', borderRadius: '999px', padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
          >
            + {item.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: '16px' }} className="announcement-builder">
        <div>
          {blocks.length === 0 ? <p style={{ color: '#d1d5db' }}>Add an element to start the page.</p> : null}
          {blocks.map((block, index) => (
            <button
              key={`${block.type}-${index}`}
              type="button"
              draggable
              className={dropIndex === index ? 'announcement-builder-row is-drop' : 'announcement-builder-row'}
              onClick={() => setSelected(index)}
              onDragStart={(event) => {
                event.dataTransfer.setData('text/aureus-index', String(index));
                event.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDropIndex(index);
              }}
              onDrop={(event) => dropOnRow(event, index)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginBottom: '8px',
                padding: '10px 12px',
                borderRadius: '10px',
                border: selected === index ? '1px solid #d4af37' : '1px solid #1f2937',
                background: '#0b0b0b',
                color: '#f9fafb',
                cursor: 'grab',
                transition: 'transform 0.2s ease, border-color 0.2s ease',
              }}
            >
              {index + 1}. {PALETTE.find((item) => item.type === block.type)?.label || block.type || 'Text'}
            </button>
          ))}
        </div>

        <div>
          {current ? (
            <div style={{ border: '1px solid #374151', borderRadius: '14px', padding: '14px', marginBottom: '16px' }}>
              <ElementFields element={current} onChange={(patch) => update(selected, patch)} onUpload={(file) => upload(selected, file)} onError={onError} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button type="button" onClick={() => move(selected, -1)} style={miniButton}>Move up</button>
                <button type="button" onClick={() => move(selected, 1)} style={miniButton}>Move down</button>
                <button type="button" onClick={() => { onChange(blocks.filter((_, index) => index !== selected)); setSelected(Math.max(0, selected - 1)); }} style={{ ...miniButton, background: '#7f1d1d', color: '#fecaca' }}>Remove</button>
              </div>
            </div>
          ) : null}
          <h3 style={{ color: '#fbbf24', margin: '0 0 8px' }}>Page preview</h3>
          <AnnouncementLayout blocks={blocks} />
        </div>
      </div>
      <style>{`@media (max-width: 800px) { .announcement-builder { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
};

function ElementFields({
  element,
  onChange,
  onUpload,
  onError,
}: {
  element: BuilderElement;
  onChange: (patch: Partial<BuilderElement>) => void;
  onUpload: (file?: File) => void;
  onError: (message: string) => void;
}) {
  if (element.type === 'heading') {
    return (
      <>
        <Field label="Heading">
          <input value={element.text || ''} maxLength={200} onChange={(event) => onChange({ text: event.target.value })} style={inputStyle} />
        </Field>
        <Field label="Size">
          <select value={element.level || 2} onChange={(event) => onChange({ level: Number(event.target.value) === 3 ? 3 : 2 })} style={inputStyle}>
            <option value={2}>Large</option>
            <option value={3}>Small</option>
          </select>
        </Field>
      </>
    );
  }
  if (element.type === 'text' || element.type === 'media' || !element.type) {
    return (
      <>
        <Field label="Text">
          <textarea value={element.text || ''} maxLength={4000} rows={4} onChange={(event) => onChange({ text: event.target.value })} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        {element.type === 'media' || element.imagePath || element.imageUrl ? <ImageFields element={element} onChange={onChange} onUpload={onUpload} /> : null}
      </>
    );
  }
  if (element.type === 'image') return <ImageFields element={element} onChange={onChange} onUpload={onUpload} />;
  if (element.type === 'button') {
    return (
      <>
        <Field label="Button label">
          <input value={element.label || ''} maxLength={80} onChange={(event) => onChange({ label: event.target.value })} style={inputStyle} />
        </Field>
        <Field label="Link">
          <input value={element.url || ''} onChange={(event) => onChange({ url: event.target.value })} placeholder="https://" style={inputStyle} />
        </Field>
      </>
    );
  }
  if (element.type === 'video') {
    return (
      <>
        {element.videoUrl ? <video src={element.videoUrl} poster={element.posterUrl || undefined} controls style={{ width: '100%', maxHeight: '220px', borderRadius: '12px', marginBottom: '10px', background: '#07090d' }} /> : null}
        <Field label="Or a YouTube or Vimeo link">
          <input value={element.url || ''} onChange={(event) => onChange({ url: event.target.value })} placeholder="https://www.youtube.com/watch?v=" style={inputStyle} />
        </Field>
      </>
    );
  }
  if (element.type === 'columns') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <ColumnStack title="Left column" items={element.left || []} onChange={(left) => onChange({ left })} onError={onError} />
        <ColumnStack title="Right column" items={element.right || []} onChange={(right) => onChange({ right })} onError={onError} />
      </div>
    );
  }
  return <p style={{ color: '#d1d5db' }}>This divider has no settings.</p>;
}

function ColumnStack({ title, items, onChange, onError }: { title: string; items: BuilderElement[]; onChange: (items: BuilderElement[]) => void; onError: (message: string) => void }) {
  return (
    <div style={{ border: '1px solid #1f2937', borderRadius: '12px', padding: '10px' }}>
      <strong style={{ color: '#fbbf24' }}>{title}</strong>
      {items.map((item, index) => (
        <div key={`${item.type}-${index}`} style={{ marginTop: '10px' }}>
          <ElementFields
            element={item}
            onChange={(patch) => onChange(items.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...patch } : entry)))}
            onError={onError}
            onUpload={async (file) => {
              if (!file) return;
              try {
                const result = fromGallery(await saveAnnouncementMedia(file));
                onChange(items.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...result, type: entry.type === 'media' ? 'media' : result.type } : entry)));
              } catch (err) {
                onError(err instanceof Error ? err.message : 'The image could not be saved.');
              }
            }}
          />
          <button type="button" onClick={() => onChange(items.filter((_, entryIndex) => entryIndex !== index))} style={miniButton}>Remove</button>
        </div>
      ))}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
        {(['heading', 'text', 'image', 'button', 'video'] as const).map((type) => (
          <button key={type} type="button" onClick={() => onChange([...items, blank(type)])} style={miniButton}>+ {type}</button>
        ))}
      </div>
    </div>
  );
}

function ImageFields({
  element,
  onChange,
  onUpload,
}: {
  element: BuilderElement;
  onChange: (patch: Partial<BuilderElement>) => void;
  onUpload: (file?: File) => void;
}) {
  return (
    <>
      {element.type === 'media' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
          {(['left', 'right', 'top', 'bottom'] as const).map((placement) => (
            <button key={placement} type="button" onClick={() => onChange({ placement })} style={{ ...miniButton, background: element.placement === placement ? '#d4af37' : '#1f2937', color: element.placement === placement ? '#111827' : '#f9fafb' }}>
              {placement}
            </button>
          ))}
        </div>
      ) : null}
      <Field label="Picture">
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => onUpload(event.target.files?.[0])} style={{ display: 'block', marginTop: '6px', color: '#d1d5db' }} />
      </Field>
      <Field label="Picture description">
        <input value={element.imageAlt || ''} maxLength={160} onChange={(event) => onChange({ imageAlt: event.target.value })} style={inputStyle} />
      </Field>
      {element.imageUrl ? <img src={element.imageUrl} alt={element.imageAlt || ''} style={{ width: '180px', height: '110px', objectFit: 'cover', borderRadius: '12px', marginBottom: '10px' }} /> : null}
    </>
  );
}

const miniButton: React.CSSProperties = {
  background: '#1f2937',
  color: '#f9fafb',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 12px',
  fontWeight: 700,
  cursor: 'pointer',
};

export default AnnouncementBuilder;
